import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import type { Asset, AssetManifest, Moment, Project, Space } from "@scenix/core";
import { ApiError, api } from "../lib/api";
import { RECOVERY_WINDOW_DAYS } from "../lib/recovery";
import { timecode } from "../lib/scrub";
import { captureStill } from "../lib/still";
import { RecoveryCountdown } from "./RecoveryCountdown";

/**
 * Everything that happens in a piece of content, on one ruler.
 *
 * Five lanes over the same seconds: what was said, where the shots cut, what
 * was heard, what was on screen, and what people concluded. The first four are
 * regenerated by the next analysis and the fifth is kept and corrected
 * (ADR 000051), so the moments lane is the only one that is editable and is
 * drawn to look unlike the others.
 *
 * Dragging across the ruler selects a range and opens the annotation form. That
 * is the whole interaction: a moment is a span somebody pointed at, and pointing
 * at a span is a drag.
 */

/** A lane's entries, reduced to what the ruler needs to draw one. */
interface Band {
  startSec: number;
  endSec: number;
  label: string;
}

interface Props {
  spaceId: Space["id"];
  assetId: Asset["id"];
  manifest: AssetManifest | null;
  moments: Moment[];
  /** Where the video is now, so the playhead and the list can follow it. */
  at: number;
  seek: (seconds: number) => void;
  /**
   * The playing element, when there is one. A still is captured from it rather
   * than generated anywhere else: it is already holding the frame somebody was
   * looking at when they decided the moment mattered (issue #110).
   */
  video: HTMLVideoElement | null;
  mayEdit: boolean;
}

export function AssetTimeline({
  spaceId,
  assetId,
  manifest,
  moments,
  at,
  seek,
  video,
  mayEdit,
}: Props) {
  const queryClient = useQueryClient();
  const ruler = useRef<HTMLDivElement>(null);

  // A drag in progress, in seconds, held as the raw ends rather than sorted:
  // dragging right to left is ordinary and sorting on every pointer move would
  // make the selection jump.
  const [dragging, setDragging] = useState<{ from: number; to: number; fromX: number } | null>(
    null,
  );
  // The range the form is open for, which outlives the drag that made it.
  const [composing, setComposing] = useState<{ startSec: number; endSec: number } | null>(null);
  const [editing, setEditing] = useState<Moment | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  // The manifest is the only thing that knows how long this is. Read before the
  // mutations below, and acted on after them: an early return here would make
  // the hook order depend on whether analysis had run.
  const duration = manifest?.durationSec ?? 0;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["asset", spaceId, assetId] });

  const create = useMutation({
    mutationFn: (draft: {
      range: { startSec: number; endSec: number };
      title: string;
      body?: string;
      tags?: string[];
    }) => api.createMoment(spaceId, assetId, draft),
    onSuccess: async (moment, draft) => {
      setComposing(null);
      setFailure(null);

      // The still is attached after the fact, so a capture that fails leaves a
      // perfectly good moment rather than losing what somebody just wrote.
      if (video) {
        const still = await captureStill(video, draft.range.startSec);
        if (still) {
          try {
            await api.putMomentThumbnail(spaceId, assetId, moment.id, still);
          } catch {
            // Not worth telling anybody about: the annotation landed, and a
            // moment with no picture is the ordinary case anyway.
          }
        }
      }
      await invalidate();
    },
    onError: (error) => setFailure(describe(error)),
  });

  const update = useMutation({
    mutationFn: (vars: { id: Moment["id"]; title: string; body: string; tags: string[] }) =>
      api.updateMoment(spaceId, assetId, vars.id, {
        title: vars.title,
        body: vars.body,
        tags: vars.tags,
      }),
    onSuccess: async () => {
      setEditing(null);
      setFailure(null);
      await invalidate();
    },
    onError: (error) => setFailure(describe(error)),
  });

  const remove = useMutation({
    mutationFn: (id: Moment["id"]) => api.deleteMoment(spaceId, assetId, id),
    onSuccess: async () => {
      setEditing(null);
      await invalidate();
    },
    onError: (error) => setFailure(describe(error)),
  });

  if (duration <= 0) {
    return (
      <p className="muted">
        No timeline yet: nothing has analysed this, so its length is unknown.
        {moments.length > 0 && " The moments below were recorded anyway."}
      </p>
    );
  }

  /** Where along the ruler a pointer is, in seconds. */
  const secondsAt = (clientX: number): number => {
    const box = ruler.current?.getBoundingClientRect();
    if (!box || box.width === 0) return 0;
    const ratio = Math.min(Math.max((clientX - box.left) / box.width, 0), 1);
    return ratio * duration;
  };

  const lanes: { key: string; label: string; bands: Band[] }[] = [
    {
      key: "transcript",
      label: "Said",
      bands: (manifest?.transcript?.segments ?? []).map((segment) => ({
        startSec: segment.startSec,
        endSec: segment.endSec,
        label: segment.text,
      })),
    },
    {
      key: "shots",
      label: "Shots",
      bands: (manifest?.shots ?? []).map((shot, i) => ({
        startSec: shot.startSec,
        endSec: shot.endSec,
        label: `Shot ${i + 1}`,
      })),
    },
    {
      key: "audio",
      label: "Heard",
      bands: (manifest?.audioEvents ?? []).map((event) => ({
        startSec: event.startSec,
        endSec: event.endSec,
        label: event.description || event.kind,
      })),
    },
    {
      key: "visuals",
      label: "Seen",
      bands: (manifest?.visuals ?? []).map((visual) => ({
        startSec: visual.startSec,
        endSec: visual.endSec,
        label: visual.description,
      })),
    },
  ];

  const percent = (seconds: number) => `${(seconds / duration) * 100}%`;

  // One shape for both, so the ruler draws a live drag and a pending form the
  // same way.
  const selection = dragging
    ? {
        startSec: Math.min(dragging.from, dragging.to),
        endSec: Math.max(dragging.from, dragging.to),
      }
    : composing;

  return (
    <section className="asset-timeline">
      {failure && (
        <p role="alert" className="error">
          {failure}
        </p>
      )}

      <div
        className="timeline-ruler"
        ref={ruler}
        role="group"
        aria-label="Asset timeline"
        onPointerDown={(event) => {
          if (!mayEdit) return;
          // Only a primary drag starts a selection; a right click belongs to
          // the browser.
          if (event.button !== 0) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          const from = secondsAt(event.clientX);
          setDragging({ from, to: from, fromX: event.clientX });
        }}
        onPointerMove={(event) => {
          if (!dragging) return;
          setDragging({ ...dragging, to: secondsAt(event.clientX) });
        }}
        onPointerUp={(event) => {
          if (!dragging) return;
          const to = secondsAt(event.clientX);
          const startSec = Math.min(dragging.from, to);
          const endSec = Math.max(dragging.from, to);
          const movedPx = Math.abs(event.clientX - dragging.fromX);
          setDragging(null);

          // A click rather than a drag, measured in pixels rather than in
          // seconds: on a ninety-minute source one pixel is several seconds, so
          // a threshold in seconds would turn an ordinary click into a form.
          if (movedPx < 3) {
            seek(startSec);
            return;
          }
          setComposing({ startSec, endSec });
        }}
      >
        {lanes.map((lane) => (
          <div key={lane.key} className={`timeline-lane is-${lane.key}`}>
            <span className="lane-label">{lane.label}</span>
            <div className="lane-track">
              {lane.bands.map((band, i) => (
                <button
                  type="button"
                  key={`${lane.key}-${i}`}
                  className={`band${band.startSec <= at && at < band.endSec ? " is-now" : ""}`}
                  style={{
                    left: percent(band.startSec),
                    width: percent(Math.max(band.endSec - band.startSec, 0.4)),
                  }}
                  title={`${timecode(band.startSec)} ${band.label}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    seek(band.startSec);
                  }}
                >
                  <span className="band-label">{band.label}</span>
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* Authored, so it sits apart from the four lanes analysis owns. */}
        <div className="timeline-lane is-moments">
          <span className="lane-label">Moments</span>
          <div className="lane-track">
            {moments.map((moment) => (
              <button
                type="button"
                key={moment.id}
                className={`band is-moment${
                  moment.range.startSec <= at && at < moment.range.endSec ? " is-now" : ""
                }${moment.authorKind === "assistant" ? " is-assistant" : ""}`}
                style={{
                  left: percent(moment.range.startSec),
                  width: percent(Math.max(moment.range.endSec - moment.range.startSec, 0.4)),
                }}
                title={`${timecode(moment.range.startSec)} ${moment.title}`}
                onClick={(event) => {
                  event.stopPropagation();
                  seek(moment.range.startSec);
                }}
              >
                <span className="band-label">{moment.title}</span>
              </button>
            ))}
          </div>
        </div>

        {selection && (
          <div
            className="timeline-selection"
            style={{
              left: percent(selection.startSec),
              width: percent(selection.endSec - selection.startSec),
            }}
          />
        )}

        <div className="timeline-playhead" style={{ left: percent(at) }} />
      </div>

      {mayEdit && !composing && (
        <p className="muted">Drag across the timeline to write down what happens there.</p>
      )}

      {composing && (
        <MomentForm
          heading={`New moment, ${timecode(composing.startSec)} to ${timecode(composing.endSec)}`}
          busy={create.isPending}
          onCancel={() => setComposing(null)}
          onSubmit={(values) =>
            create.mutate({
              range: { startSec: composing.startSec, endSec: composing.endSec },
              ...values,
            })
          }
        />
      )}

      {editing && (
        <MomentForm
          heading={`Editing, ${timecode(editing.range.startSec)} to ${timecode(editing.range.endSec)}`}
          initial={{ title: editing.title, body: editing.body ?? "", tags: editing.tags }}
          busy={update.isPending}
          onCancel={() => setEditing(null)}
          onDelete={() => {
            if (window.confirm(`Delete "${editing.title}"?`)) remove.mutate(editing.id);
          }}
          onSubmit={(values) =>
            update.mutate({
              id: editing.id,
              title: values.title,
              body: values.body ?? "",
              tags: values.tags ?? [],
            })
          }
        />
      )}

      <MomentList
        spaceId={spaceId}
        moments={moments}
        at={at}
        seek={seek}
        mayEdit={mayEdit}
        onEdit={setEditing}
      />

      {mayEdit && <DeletedMoments spaceId={spaceId} assetId={assetId} />}
    </section>
  );
}

/**
 * The other side of the recovery window, on the asset the moments belong to
 * (ADR 000055).
 *
 * Folded away rather than a screen of its own, the way the Deleted listing on
 * the Content page is: a deleted moment is looked for from the asset somebody
 * was already reading, and a page that has to be navigated to is one nobody
 * finds while they still can.
 *
 * Only fetched once it is opened. Almost every asset has nothing here, and the
 * asset page should not pay a request per visit to say so.
 *
 * No still per row, unlike the live list. A deleted moment is invisible all the
 * way down, its thumbnail endpoint included, so a row that tried to draw one
 * would draw a broken image. What identifies it here is where it sat and what
 * it said.
 */
function DeletedMoments({ spaceId, assetId }: { spaceId: Space["id"]; assetId: Asset["id"] }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const deleted = useQuery({
    queryKey: ["moments", spaceId, assetId, "deleted"],
    queryFn: () => api.listMoments(spaceId, assetId, { deleted: true }),
    enabled: open,
  });

  const restore = useMutation({
    mutationFn: (id: Moment["id"]) => api.restoreMoment(spaceId, assetId, id),
    onSuccess: async () => {
      setFailure(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["moments", spaceId, assetId, "deleted"] }),
        queryClient.invalidateQueries({ queryKey: ["asset", spaceId, assetId] }),
      ]);
    },
    onError: (error) => setFailure(describe(error)),
  });

  if (!open) {
    return (
      <button type="button" className="ghost" onClick={() => setOpen(true)}>
        Deleted moments
      </button>
    );
  }

  return (
    <div className="deleted-moments">
      <button type="button" className="ghost" onClick={() => setOpen(false)}>
        Hide deleted
      </button>
      <p className="muted">
        A deleted moment can be brought back for {RECOVERY_WINDOW_DAYS} days. After that it is
        removed for good, its still with it.
      </p>
      {failure && (
        <p role="alert" className="error">
          {failure}
        </p>
      )}

      {deleted.isPending ? (
        <p className="muted">Loading…</p>
      ) : deleted.error ? (
        <p className="error">{describe(deleted.error)}</p>
      ) : deleted.data.length === 0 ? (
        <p className="muted">Nothing deleted right now.</p>
      ) : (
        <ul className="deleted-moment-list">
          {deleted.data.map((moment) => (
            <li key={moment.id}>
              <span className="moment-when">{timecode(moment.range.startSec)}</span>
              <span className="moment-body">
                <strong>{moment.title}</strong>
                <RecoveryCountdown deletedAt={moment.deletedAt} />
              </span>
              <button
                type="button"
                className="ghost"
                disabled={restore.isPending}
                onClick={() => restore.mutate(moment.id)}
              >
                Restore
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The list beside the ruler, which highlights whatever the video is inside
 * right now. The same job the transcript already does, for the lane a person
 * wrote.
 */
function MomentList({
  spaceId,
  moments,
  at,
  seek,
  mayEdit,
  onEdit,
}: {
  spaceId: Space["id"];
  moments: Moment[];
  at: number;
  seek: (seconds: number) => void;
  mayEdit: boolean;
  onEdit: (moment: Moment) => void;
}) {
  if (moments.length === 0) {
    return <p className="muted">Nothing written down about this yet.</p>;
  }

  return (
    <ul className="moment-list">
      {moments.map((moment) => {
        const now = moment.range.startSec <= at && at < moment.range.endSec;
        return (
          <li key={moment.id} className={now ? "is-now" : ""}>
            <button
              type="button"
              className="moment-when"
              onClick={() => seek(moment.range.startSec)}
            >
              {timecode(moment.range.startSec)}
            </button>
            {moment.thumbnailPath ? (
              <img
                className="moment-still"
                src={api.momentThumbnailUrl(spaceId, moment.assetId, moment.id)}
                alt=""
              />
            ) : (
              <span className="moment-still is-empty" />
            )}
            <span className="moment-body">
              <strong>{moment.title}</strong>
              {moment.authorKind === "assistant" && (
                <>
                  {/* JSX emits no space around an element on its own line, so
                      without this the badge reads as the last word of the
                      title (issue #310). */}{" "}
                  <span className="badge" title="Written by an agent, not a person">
                    agent
                  </span>
                </>
              )}
              {moment.body && <span className="muted">{moment.body}</span>}
              {moment.tags.length > 0 && (
                <span className="moment-tags">
                  {moment.tags.map((tag) => (
                    <span className="tag" key={tag}>
                      {tag}
                    </span>
                  ))}
                </span>
              )}
            </span>
            {mayEdit && (
              <span className="row">
                <SendToProject spaceId={spaceId} moment={moment} />
                <button type="button" className="ghost" onClick={() => onEdit(moment)}>
                  Edit
                </button>
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Sends one moment to a project's shortlist (ADR 000051).
 *
 * A select rather than a menu, because the list is every project in the space
 * and a native control gets keyboard handling, overflow and long names right
 * without any of it being written here. It resets to the prompt after a send,
 * since it is a verb rather than a setting: sending the same moment twice is
 * idempotent, so a second send to the same project is harmless.
 */
function SendToProject({ spaceId, moment }: { spaceId: Space["id"]; moment: Moment }) {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  // Every project in the space, which is the whole point: a moment is worth
  // sending to the project being cut, not only to the one you came from.
  const projects = useQuery({
    queryKey: ["projects", spaceId],
    queryFn: () => api.listProjects(spaceId),
  });

  const send = useMutation({
    mutationFn: (projectId: Project["id"]) =>
      api.addToShortlist(spaceId, projectId, { assetId: moment.assetId, momentId: moment.id }),
    onError: (error: unknown) => setFailure(describe(error)),
  });

  const available = projects.data ?? [];
  if (available.length === 0) return null;

  return (
    <span className="send-to-project">
      <label>
        <span className="visually-hidden">Send "{moment.title}" to a project</span>
        <select
          value=""
          disabled={send.isPending}
          onChange={(event) => {
            const projectId = event.target.value as Project["id"];
            if (!projectId) return;
            const name = available.find((project) => project.id === projectId)?.name ?? "project";
            setFailure(null);
            send.mutate(projectId, { onSuccess: () => setSentTo(name) });
          }}
        >
          <option value="">Send to…</option>
          {available.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
      </label>
      {failure ? (
        <span role="alert" className="error">
          {failure}
        </span>
      ) : (
        sentTo && <span className="muted">Sent to {sentTo}</span>
      )}
    </span>
  );
}

interface FormValues {
  title: string;
  body?: string;
  tags?: string[];
}

function MomentForm({
  heading,
  initial,
  busy,
  onSubmit,
  onCancel,
  onDelete,
}: {
  heading: string;
  initial?: { title: string; body: string; tags: string[] };
  busy: boolean;
  onSubmit: (values: FormValues) => void;
  onCancel: () => void;
  onDelete?: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [body, setBody] = useState(initial?.body ?? "");
  const [tags, setTags] = useState((initial?.tags ?? []).join(", "));

  return (
    <form
      className="moment-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!title.trim()) return;
        onSubmit({
          title: title.trim(),
          body: body.trim(),
          tags: tags
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean),
        });
      }}
    >
      <h3>{heading}</h3>
      <input
        autoFocus
        aria-label="What happens here"
        placeholder="What happens here"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        maxLength={200}
      />
      <textarea
        aria-label="More, if there is more"
        placeholder="More, if there is more"
        value={body}
        onChange={(event) => setBody(event.target.value)}
        rows={3}
      />
      <input
        aria-label="Tags, comma separated"
        placeholder="Tags, comma separated"
        value={tags}
        onChange={(event) => setTags(event.target.value)}
      />
      <div className="row">
        <button type="submit" disabled={!title.trim() || busy}>
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" className="ghost" onClick={onCancel}>
          Cancel
        </button>
        {onDelete && (
          <button type="button" className="ghost danger" onClick={onDelete}>
            Delete
          </button>
        )}
      </div>
    </form>
  );
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.userMessage;
  return error instanceof Error ? error.message : "Something went wrong.";
}
